import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowUpRight, BadgeCheck, BookOpen, CheckCircle2, ChevronDown, Clock3, Gauge,
  Instagram, MapPin, Menu, MessageCircle, Phone, Send, ShieldCheck, Star, UserRound, Wrench, X,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONTACT_PHONE = "05375022181";
const CONTACT_PHONE_DISPLAY = "0537 502 21 81";
const CONTACT_ADDRESS = "Azmimilli Mahallesi, Darıcı Sokak No:18, Düzce Merkez";
const CONTACT_HOURS = "Her gün · 08:00 — 20:00";
const WHATSAPP_LINK = "https://wa.me/905375022181?text=Merhaba%20Nova%20Teknik%2C%20cihaz%20ar%C4%B1zam%20i%C3%A7in%20servis%20talebinde%20bulunmak%20istiyorum.";
const INSTAGRAM_LINK = "https://www.instagram.com/novateknikduzce_";
const MAPS_LINK = "https://www.google.com/maps/search/?api=1&query=Nova+Teknik+Azmimilli+Mahallesi+Darıcı+Sokak+No:18+Düzce";
const MAPS_EMBED = "https://www.google.com/maps?q=Nova%20Teknik%20Azmimilli%20Mahallesi%20Dar%C4%B1c%C4%B1%20Sokak%20No%3A18%20D%C3%BCzce&z=16&output=embed";

const IMAGES = {
  hero: "/photos/klima-gaz.jpg",
  washing: "/photos/camasir.jpg",
  ac: "/photos/klima-units.jpg",
  fridge: "/photos/buzdolabi.jpg",
  dishwasher: "/photos/onarim.jpg",
  tools: "https://images.unsplash.com/photo-1566527379347-e5e3c8d905e1?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
};

const SERVICES = [
  { slug: "camasir-makinesi-servisi-duzce", title: "Çamaşır Makinesi Servisi", tag: "DÜZCE ÇAMAŞIR MAKİNESİ SERVİSİ", desc: "Su boşaltmama, sıkmada ses yapma, kazan dönmeme ve kart arızaları aynı gün yerinde onarılır.", img: IMAGES.washing },
  { slug: "buzdolabi-tamiri-duzce", title: "Buzdolabı Tamir & Bakım", tag: "DÜZCE BUZDOLABI TAMİRCİSİ", desc: "Soğutmama, gaz kaçağı, kompresör değişimi ve no-frost fan arızalarına hızlı müdahale.", img: IMAGES.fridge },
  { slug: "klima-servisi-duzce", title: "Klima Bakım & Onarım", tag: "DÜZCE KLİMA SERVİSİ", desc: "Gaz dolumu, filtre hijyen bakımı, kart tamiri ve montaj-demontaj hizmetleri.", img: IMAGES.ac },
  { slug: "bulasik-makinesi-servisi-duzce", title: "Bulaşık Makinesi Servisi", tag: "DÜZCE BULAŞIK MAKİNESİ TAMİRİ", desc: "Temiz yıkamama, su almama ve pompa tıkanıklığı arızaları profesyonel ekipmanla çözülür.", img: "/photos/bulasik.jpg" },
  { slug: "ankastre-servisi-duzce", title: "Ankastre, Davlumbaz, Fırın & Ocak", tag: "DÜZCE ANKASTRE SERVİSİ", desc: "Ankastre fırın, ocak ve davlumbaz bakım-onarımı; rezistans, kart ve motor arızaları yerinde çözülür.", img: "/photos/ankastre.jpg" },
  { slug: "kucuk-ev-aletleri-duzce", title: "Küçük Ev Aletleri Tamiri", tag: "DÜZCE KÜÇÜK EV ALETLERİ", desc: "Süpürge motor değişimi, ütü, mikrodalga, kahve makinesi ve robot süpürge onarımı.", img: "/photos/atolye.jpg" },
];

const CHAPTERS = [
  { num: "01", title: "Hızlı & Nokta Atışı Teşhis", desc: "Arızalı cihazınız için Düzce Merkez adresinize mobil ekibimizi yönlendiriyor, arızayı yerinde tespit ediyoruz." },
  { num: "02", title: "%100 Orijinal Yedek Parça", desc: "Çamaşır makinesi, buzdolabı ve klimalarınızda sadece fabrika onaylı orijinal parçalar kullanıyoruz." },
  { num: "03", title: "1 Yıl İşçilik & Parça Garantisi", desc: "Değiştirilen tüm parçalar ve yapılan işlemler 12 ay boyunca Nova Teknik garantisi altındadır." },
  { num: "04", title: "Yerinde & Şeffaf Fiyatlandırma", desc: "Cihazınızı gözünüzün önünde test edip net fiyat veriyoruz. Onayınız olmadan hiçbir işlem yapmıyoruz." },
];

const FAQS = [
  ["Düzce merkezde hangi bölgelere servis veriyorsunuz?", "Azmimilli, Camikebir, Cedidiye, Kültür, Konuralp ve tüm merkez mahallelerine mobil ekibimizle hizmet veriyoruz."],
  ["Aynı gün servis mümkün mü?", "Talebinizi oluşturduğunuzda uygunluk durumuna göre aynı gün veya size en uygun ilk zaman için planlama yapıyoruz."],
  ["Servis ücreti nasıl belirleniyor?", "Cihazı ve arızayı yerinde inceledikten sonra işlem öncesi net fiyat veriyoruz. Onayınız olmadan işlem yapmıyoruz."],
  ["Yaptığınız işlemler garantili mi?", "Evet. Değişen parçalar ve işçilik 1 yıl Nova Teknik garantisi kapsamındadır."],
];

const MAHALLELER = ["Azmimilli", "Camikebir", "Cedidiye", "Kültür", "Konuralp", "Bahçelievler", "Şerefiye", "Körpeşler", "Diğer"];
const DEVICE_OPTIONS = ["Çamaşır Makinesi", "Bulaşık Makinesi", "Buzdolabı", "Klima", "Ankastre / Fırın / Ocak / Davlumbaz", "Küçük Ev Aleti", "Diğer"];
const TIME_OPTIONS = ["En kısa sürede", "Sabah (08:00-12:00)", "Öğleden sonra (12:00-17:00)", "Akşam (17:00-20:00)"];
const MARQUEE_TEXT = "ÇAMAŞIR MAKİNESİ SERVİSİ ✦ BUZDOLABI TAMİRİ ✦ KLİMA BAKIM & ONARIM ✦ ANKASTRE & DAVLUMBAZ ✦ BULAŞIK MAKİNESİ ✦ KÜÇÜK EV ALETLERİ ✦ DÜZCE MERKEZ ✦ AYNI GÜN YERİNDE SERVİS ✦ ";

const GoogleIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M21.35 11.1H12v2.9h5.35c-.5 2.4-2.6 4-5.35 4a6 6 0 1 1 0-12c1.5 0 2.9.55 3.95 1.45l2.2-2.2A9.4 9.4 0 1 0 12 21.4c5.4 0 9-3.8 9-9.15 0-.4-.05-.8-.15-1.15Z" /></svg>
);

const fadeUp = {
  hidden: { opacity: 0, y: 42 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.75, delay: i * 0.09, ease: [0.16, 1, 0.3, 1] } }),
};

const Reveal = ({ children, className, delay = 0 }) => (
  <motion.div className={className} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-70px" }} variants={fadeUp} custom={delay}>
    {children}
  </motion.div>
);

export default function Landing() {
  const { user, login } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [form, setForm] = useState({ name: "", phone: "", mahalle: "Azmimilli", service: "Çamaşır Makinesi", note: "", preferred_time: "En kısa sürede", email: "" });
  const [sending, setSending] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const lenisRef = useRef(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  useEffect(() => {
    document.title = "Nova Teknik Düzce | Beyaz Eşya ve Klima Servisi";
    document.querySelector('meta[name="description"]')?.setAttribute("content", "Nova Teknik Düzce: Düzce beyaz eşya servisi, Düzce klima servisi ve Düzce çamaşır makinesi tamiri. Aynı gün yerinde onarım, 1 yıl işçilik garantisi. Hemen arayın: 0537 502 21 81.");
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    let rafId;
    const raf = (time) => { lenis.raf(time); rafId = requestAnimationFrame(raf); };
    rafId = requestAnimationFrame(raf);
    if (window.location.hash) {
      const target = document.getElementById(window.location.hash.slice(1));
      if (target) setTimeout(() => lenis.scrollTo(target, { offset: -70 }), 400);
    }
    return () => { cancelAnimationFrame(rafId); lenis.destroy(); };
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      if (lenisRef.current) lenisRef.current.scrollTo(el, { offset: -70 });
      else el.scrollIntoView({ behavior: "smooth" });
    }
    setMenuOpen(false);
  };

  const onCardMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - rect.top}px`);
  };

  const submitRequest = async (event) => {
    event.preventDefault();
    if (!form.name || !form.phone || !form.service || !form.note) {
      toast.error("Lütfen tüm alanları doldurun.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/service-requests`, form, { withCredentials: true });
      toast.success("Talebiniz alındı. Nova Teknik en kısa sürede sizi arayacak.");
      setForm({ name: "", phone: "", mahalle: "Azmimilli", service: "Çamaşır Makinesi", note: "", preferred_time: "En kısa sürede", email: "" });
      setFormSuccess(true);
    } catch {
      toast.error("Talep gönderilemedi. Lütfen bizi telefonla arayın.");
    } finally {
      setSending(false);
    }
  };

  const LoginButton = ({ testid, className = "btn-login" }) => (
    user ? (
      <Link to="/panel" className={className} data-testid={testid}><UserRound size={15} /> Panelim</Link>
    ) : (
      <button className={className} data-testid={testid} onClick={login}><GoogleIcon /> Google ile Giriş</button>
    )
  );

  const heroLines = [
    <>Bozulan cihazınız,</>,
    <><span className="accent">aynı gün</span> yerinde</>,
    <>onarılır.</>,
  ];

  return (
    <div className="site-shell" data-testid="nova-teknik-site">
      <div className="noise-overlay" aria-hidden="true" />

      <header className="site-header">
        <div className="container header-inner">
          <button className="brand" data-testid="brand-home-button" onClick={() => scrollTo("anasayfa")}>
            <img className="brand-logo" src="/logo.png" alt="Nova Teknik Servis Düzce" />
          </button>
          <nav className={`nav-links ${menuOpen ? "open" : ""}`} data-testid="main-navigation">
            <button data-testid="nav-services-link" onClick={() => scrollTo("hizmetler")}>Hizmetler</button>
            <button data-testid="nav-manifesto-link" onClick={() => scrollTo("manifesto")}>Manifesto</button>
            <button data-testid="nav-faq-link" onClick={() => scrollTo("sss")}>S.S.S.</button>
            <button data-testid="nav-contact-link" onClick={() => scrollTo("iletisim")}>İletişim</button>
            <Link to="/rehber" data-testid="nav-guide-link">Rehber</Link>
            <div className="nav-mobile-actions">
              <LoginButton testid="mobile-login-button" />
              <a className="btn-call" data-testid="mobile-call-button" href={`tel:${CONTACT_PHONE}`}><Phone size={15} /> {CONTACT_PHONE_DISPLAY}</a>
            </div>
          </nav>
          <div className="header-actions">
            <span className="status-badge" data-testid="open-status-badge"><span className="pulse-dot" /> AÇIK · 08:00–20:00</span>
            <LoginButton testid="header-login-button" />
            <a className="btn-call" data-testid="nav-call-button" href={`tel:${CONTACT_PHONE}`}><Phone size={15} /> <span className="call-text">{CONTACT_PHONE_DISPLAY}</span></a>
            <button className="menu-button" data-testid="mobile-menu-button" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menü">{menuOpen ? <X size={20} /> : <Menu size={20} />}</button>
          </div>
        </div>
      </header>

      <main>
        <section className="hero-section container" id="anasayfa" ref={heroRef} data-testid="hero-section">
          <div className="hero-grid-bg" aria-hidden="true" />
          <div className="hero-copy">
            <motion.p className="hero-eyebrow" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.1 }} data-testid="hero-eyebrow">
              DÜZCE MERKEZ — BEYAZ EŞYA · KLİMA · KÜÇÜK EV ALETLERİ
            </motion.p>
            <h1 className="hero-title" data-testid="hero-title">
              {heroLines.map((line, i) => (
                <span className="line-mask" key={i}>
                  <motion.span className="line-inner" initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 0.95, delay: 0.25 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}>
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p className="hero-lede" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}>
              Düzce çamaşır makinesi servisi, buzdolabı tamiri, klima bakım-onarım ve ankastre servisi.
              Mobil ekibimiz adresinize gelir, arızayı gözünüzün önünde test eder; onayınız olmadan işlem yapmaz.
            </motion.p>
            <motion.div className="hero-actions" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}>
              <button className="btn-primary" data-testid="hero-service-button" onClick={() => scrollTo("servis-talebi")}>
                Servis Talebi Oluştur <ArrowUpRight size={17} />
              </button>
              <a className="btn-ghost" data-testid="hero-whatsapp-button" href={WHATSAPP_LINK} target="_blank" rel="noreferrer">
                <MessageCircle size={17} /> WhatsApp Destek
              </a>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.05 }}>
              <span className="kesif-badge" data-testid="kesif-badge">
                <ShieldCheck size={16} /> Yerinde teşhis — işlem öncesi net fiyat, onaysız işlem yok
              </span>
            </motion.div>
            <motion.div className="hero-stats" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 1.1 }}>
              <div data-testid="hero-stat-0"><strong>7 gün</strong><span>08:00–20:00 açık</span></div>
              <div data-testid="hero-stat-1"><strong>1 yıl</strong><span>işçilik garantisi</span></div>
              <div data-testid="hero-stat-2"><strong>Düzce</strong><span>merkez mobil ekip</span></div>
            </motion.div>
          </div>
          <motion.div className="hero-visual" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}>
            <div className="hero-frame">
              <span className="corner tl" /><span className="corner tr" /><span className="corner bl" /><span className="corner br" />
              <motion.img style={{ y: imgY }} src={IMAGES.hero} alt="Nova Teknik beyaz eşya servis teknisyeni Düzce" data-testid="hero-image" />
              <div className="hero-badge">
                <BadgeCheck size={20} />
                <span><b>Orijinal parça</b><small>+ 1 yıl garanti</small></span>
              </div>
              <div className="hero-tag">NOVA TEKNİK · DÜZCE MERKEZ</div>
            </div>
          </motion.div>
        </section>

        <div className="marquee" data-testid="editorial-marquee" aria-hidden="true">
          <div className="marquee-track">
            <span>{MARQUEE_TEXT}</span>
            <span>{MARQUEE_TEXT}</span>
          </div>
        </div>

        <section className="manifesto container" id="manifesto" data-testid="manifesto-section">
          <Reveal className="section-head">
            <div>
              <div className="kicker">SERVİS MANİFESTOSU</div>
              <h2>İşimiz cihazı değil,<br /><span className="accent">güveni</span> onarmak.</h2>
            </div>
            <p>Her servis ziyaretinde aynı dört ilkeyle çalışırız: doğru teşhis, orijinal parça, garanti ve şeffaf fiyat.</p>
          </Reveal>
          <div className="manifesto-grid">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.num} className="chapter" delay={i} >
                <div data-testid={`manifesto-chapter-${i}`}>
                  <span className="chapter-num">{c.num}</span>
                  <h3>{c.title}</h3>
                  <p>{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="services-section container" id="hizmetler" data-testid="services-section">
          <Reveal className="section-head">
            <div>
              <div className="kicker">NE YAPIYORUZ?</div>
              <h2>Her cihazın bir<br /><span className="accent">çözümü</span> var.</h2>
            </div>
            <p>Düzce beyaz eşya servisi ve klima servisi olarak evinizin tüm teknik ihtiyaçlarına tek ekipten destek veriyoruz.</p>
          </Reveal>
          <div className="services-grid">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} className="service-card-wrap" delay={i % 3}>
                <article className="service-card" onMouseMove={onCardMove} data-testid={`service-card-${i}`}>
                  <div className="service-media"><img src={s.img} alt={s.title + " Düzce"} loading="lazy" /></div>
                  <div className="service-body">
                    <span className="service-tag">{s.tag}</span>
                    <h3>{s.title}</h3>
                    <p>{s.desc}</p>
                    <div className="service-actions">
                      <button className="service-link" data-testid={`service-detail-button-${i}`} onClick={() => scrollTo("servis-talebi")}>
                        Talep oluştur <ArrowUpRight size={14} />
                      </button>
                      <Link className="service-link cyan" data-testid={`service-page-link-${i}`} to={`/${s.slug}`}>
                        Detaylı incele <ArrowUpRight size={14} />
                      </Link>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="reviews-section container" data-testid="reviews-section">
          <Reveal className="section-head">
            <div>
              <div className="kicker">MÜŞTERİ YORUMLARI</div>
              <h2>Düzce bize<br /><span className="accent">güveniyor.</span></h2>
            </div>
            <a className="btn-ghost slim" data-testid="google-review-button" href="https://share.google/MGPVxZGVfYERKVPbD" target="_blank" rel="noreferrer">
              Google'da yorum bırakın <ArrowUpRight size={14} />
            </a>
          </Reveal>
          <Reveal>
            <div className="rating-banner" data-testid="google-rating-banner">
              <div className="rating-score">
                <strong>5.0</strong>
                <span className="review-stars">{[...Array(5)].map((_, s) => <Star key={s} size={20} fill="currentColor" />)}</span>
                <small>26 gerçek Google yorumu</small>
              </div>
              <p>Müşterilerimizin gerçek yorumlarını Google Haritalar'daki işletme profilimizde okuyabilir, siz de deneyiminizi paylaşabilirsiniz.</p>
              <a className="btn-primary slim" data-testid="google-reviews-read-link" href="https://share.google/MGPVxZGVfYERKVPbD" target="_blank" rel="noreferrer">
                Yorumları Google'da oku <ArrowUpRight size={15} />
              </a>
            </div>
          </Reveal>
        </section>

        <section className="request-section" id="servis-talebi" data-testid="service-request-section">
          <div className="container request-grid">
            <Reveal className="request-intro">
              <div className="kicker">HIZLI SERVİS</div>
              <h2>Arızayı anlatın,<br /><span className="accent">gerisini</span> bize bırakın.</h2>
              <p>Formu doldurun, size ulaşalım. {user ? "Giriş yaptığınız için talebiniz panelinizde de görünecek." : "Google ile giriş yaparsanız talebinizi panelden takip edebilirsiniz."}</p>
              <span className="kesif-badge" data-testid="form-kesif-badge">
                <ShieldCheck size={16} /> Yerinde teşhis — fiyat, işlem öncesi onayınıza sunulur
              </span>
              <div className="request-contacts">
                <a className="request-contact" data-testid="request-whatsapp-link" href={WHATSAPP_LINK} target="_blank" rel="noreferrer">
                  <MessageCircle size={19} /><span>WhatsApp'tan hemen ulaşın<b>{CONTACT_PHONE_DISPLAY}</b></span>
                </a>
                <a className="request-contact" data-testid="request-call-link" href={`tel:${CONTACT_PHONE}`}>
                  <Phone size={19} /><span>Telefonla arayın<b>{CONTACT_PHONE_DISPLAY}</b></span>
                </a>
              </div>
            </Reveal>
            <Reveal delay={1}>
              {formSuccess ? (
                <div className="request-success" data-testid="service-request-success">
                  <CheckCircle2 size={44} />
                  <h3>Teşekkür ederiz!</h3>
                  <p>Servis talebiniz bize ulaştı. Ekibimiz en kısa sürede sizi arayacak.</p>
                  <div className="success-actions">
                    <button className="btn-ghost" data-testid="new-service-request-button" onClick={() => setFormSuccess(false)}>Yeni talep oluştur</button>
                    {user && <Link to="/panel" className="btn-primary slim" data-testid="go-panel-button">Panelimde görüntüle</Link>}
                  </div>
                </div>
              ) : (
                <form className="request-form" onSubmit={submitRequest} data-testid="service-request-form">
                  <label>Adınız Soyadınız
                    <input required data-testid="service-request-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Adınızı yazın" />
                  </label>
                  <label>Telefon Numaranız
                    <input required type="tel" data-testid="service-request-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="05__ ___ __ __" />
                  </label>
                  <label>Mahalle
                    <select data-testid="service-request-mahalle-select" value={form.mahalle} onChange={(e) => setForm({ ...form, mahalle: e.target.value })}>
                      {MAHALLELER.map((m) => <option key={m}>{m}</option>)}
                    </select>
                  </label>
                  <label>Cihaz Türü
                    <select required data-testid="service-request-service-select" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })}>
                      {DEVICE_OPTIONS.map((d) => <option key={d}>{d}</option>)}
                    </select>
                  </label>
                  <label>E-posta (isteğe bağlı — durum bildirimi için)
                    <input type="email" data-testid="service-request-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="ornek@eposta.com" />
                  </label>
                  <label>Tercih Edilen Servis Saati
                    <select data-testid="service-request-time-select" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })}>
                      {TIME_OPTIONS.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </label>
                  <label className="full">Arıza Açıklaması
                    <textarea required rows="3" data-testid="service-request-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Cihazın arızasını kısaca anlatın..." />
                  </label>
                  <button className="form-submit" data-testid="service-request-submit-button" disabled={sending}>
                    {sending ? "Gönderiliyor..." : "Servis talebi gönder"} <Send size={16} />
                  </button>
                  <small className="form-note"><ShieldCheck size={14} /> Bilgileriniz yalnızca size ulaşmak için kullanılır.</small>
                </form>
              )}
            </Reveal>
          </div>
        </section>

        <section className="local-seo" data-testid="local-seo-section">
          <div className="container">
            <Reveal>
              <div className="kicker">SERVİS BÖLGELERİMİZ</div>
              <h2 className="local-title">Düzce Merkez'in her mahallesindeyiz.</h2>
              <div className="local-chips" data-testid="local-chips">
                {["Azmimilli", "Camikebir", "Cedidiye", "Kültür", "Konuralp", "Bahçelievler", "Şerefiye", "Körpeşler", "Nasırlı", "Yeşiltepe", "Uzunmustafa", "Fevzi Çakmak"].map((m) => <span key={m}>{m.toUpperCase()} MAH.</span>)}
              </div>
            </Reveal>
          </div>
        </section>

        <section className="faq-section container" id="sss" data-testid="faq-section">
          <Reveal className="faq-title">
            <div className="kicker">MERAK EDİLENLER</div>
            <h2>Sorunuz mu var?<br /><span className="accent">Yanıt</span> burada.</h2>
            <p>Servis süreciyle ilgili en sık sorulanları sizin için cevapladık.</p>
          </Reveal>
          <div className="faq-list">
            {FAQS.map(([q, a], i) => (
              <Reveal key={q} delay={i}>
                <div className={`faq-item ${openFaq === i ? "active" : ""}`}>
                  <button data-testid={`faq-button-${i}`} onClick={() => setOpenFaq(openFaq === i ? -1 : i)}>
                    <span className="qnum">0{i + 1}</span>{q}<ChevronDown size={18} />
                  </button>
                  {openFaq === i && <p data-testid={`faq-answer-${i}`}>{a}</p>}
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      </main>

      <footer className="footer" id="iletisim" data-testid="footer-contact">
        <div className="container">
          <div className="footer-word" aria-hidden="true">NOVA TEKNİK</div>
          <div className="footer-map" data-testid="footer-map">
            <iframe title="Nova Teknik Düzce konum haritası" src={MAPS_EMBED} loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
            <a className="map-open" data-testid="map-open-link" href={MAPS_LINK} target="_blank" rel="noreferrer"><MapPin size={14} /> Google Haritalar'da aç</a>
          </div>
          <div className="footer-grid">
            <div>
              <div className="kicker">NOVA TEKNİK</div>
              <p className="footer-desc">Düzce Merkez'de beyaz eşya, klima, ankastre ve küçük ev aletlerinde güvenilir teknik servis.</p>
              <div className="footer-social">
                <LoginButton testid="footer-login-button" className="btn-login" />
                <a className="btn-social" data-testid="instagram-follow-button" href={INSTAGRAM_LINK} target="_blank" rel="noreferrer">
                  <Instagram size={15} /> Bizi Instagram'da takip edin
                </a>
              </div>
            </div>
            <div>
              <div className="kicker">İLETİŞİM</div>
              <a className="footer-link" data-testid="contact-phone-link" href={`tel:${CONTACT_PHONE}`}><Phone size={15} /> {CONTACT_PHONE_DISPLAY}</a>
              <a className="footer-link" data-testid="contact-whatsapp-link" href={WHATSAPP_LINK} target="_blank" rel="noreferrer"><MessageCircle size={15} /> WhatsApp'tan yazın</a>
              <a className="footer-link" data-testid="contact-instagram-link" href={INSTAGRAM_LINK} target="_blank" rel="noreferrer"><Instagram size={15} /> Instagram: @novateknikduzce_</a>
              <a className="footer-link" data-testid="contact-maps-link" href="https://share.google/MGPVxZGVfYERKVPbD" target="_blank" rel="noreferrer"><MapPin size={15} /> Google Haritalar & İşletme Profili</a>
              <Link className="footer-link" data-testid="footer-guide-link" to="/rehber"><BookOpen size={15} /> Arıza Rehberi</Link>
            </div>
            <div>
              <div className="kicker">SERVİS ADRESİ</div>
              <p className="footer-info" data-testid="contact-location-info"><MapPin size={15} /> {CONTACT_ADDRESS}</p>
              <p className="footer-info" data-testid="contact-hours-info"><Clock3 size={15} /> {CONTACT_HOURS}</p>
            </div>
          </div>
          <div className="footer-bottom">
            <span>© 2026 Nova Teknik. Tüm hakları saklıdır.</span>
            <span>Düzce çamaşır makinesi servisi · beyaz eşya tamiri · klima servisi</span>
          </div>
        </div>
      </footer>

      <a className="float-whatsapp" data-testid="float-whatsapp-button" href={WHATSAPP_LINK} target="_blank" rel="noreferrer" aria-label="WhatsApp">
        <MessageCircle size={22} />
      </a>
    </div>
  );
}
